import type { CreateVehicleDTO, IVehicleRepository, Vehicle } from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiVehicleResponse {
  id: string;
  // Backend returns the owning tenant member as `memberId`; older payloads used `userId`.
  memberId?: string;
  userId?: string;
  unitId?: string | null;
  ownerName?: string | null;
  plateNumber: string;
  brand: string;
  model: string;
  year?: number | null;
  color?: string | null;
  type: 'CAR' | 'MOTORCYCLE' | 'TRUCK' | 'SUV' | 'VAN' | 'OTHER';
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  vin?: string | null;
  insurancePolicy?: string | null;
  insuranceExpiry?: string | null;
  photoUrl?: string | null;
  notes?: string | null;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiCreateVehicleRequest {
  plateNumber: string;
  brand: string;
  model: string;
  year?: number;
  color?: string;
  type: 'CAR' | 'MOTORCYCLE' | 'TRUCK' | 'SUV' | 'VAN' | 'OTHER';
  vin?: string;
  insurancePolicy?: string;
  insuranceExpiry?: string;
  notes?: string;
  isPrimary?: boolean;
  unitId?: string;
}

interface ApiUpdateVehicleRequest {
  plateNumber?: string;
  brand?: string;
  model?: string;
  year?: number;
  color?: string;
  type?: 'CAR' | 'MOTORCYCLE' | 'TRUCK' | 'SUV' | 'VAN' | 'OTHER';
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  vin?: string;
  insurancePolicy?: string;
  insuranceExpiry?: string;
  photoUrl?: string;
  notes?: string;
  isPrimary?: boolean;
}

// Mapea la respuesta de la API al tipo Vehicle interno
const mapApiVehicleToVehicle = (apiVehicle: ApiVehicleResponse): Vehicle => ({
  id: apiVehicle.id,
  userId: apiVehicle.memberId ?? apiVehicle.userId ?? '',
  unitId: apiVehicle.unitId ?? null,
  ownerName: apiVehicle.ownerName ?? null,
  plateNumber: apiVehicle.plateNumber,
  brand: apiVehicle.brand,
  model: apiVehicle.model,
  year: apiVehicle.year || undefined,
  color: apiVehicle.color || undefined,
  type: apiVehicle.type,
  status: apiVehicle.status,
  vin: apiVehicle.vin || undefined,
  insurancePolicy: apiVehicle.insurancePolicy || undefined,
  insuranceExpiry: apiVehicle.insuranceExpiry || undefined,
  photoUrl: apiVehicle.photoUrl || undefined,
  notes: apiVehicle.notes || undefined,
  isPrimary: apiVehicle.isPrimary,
  createdAt: apiVehicle.createdAt,
  updatedAt: apiVehicle.updatedAt,
});

export class ApiVehicleRepository implements IVehicleRepository {
  async getAllVehicles(): Promise<Vehicle[]> {
    const client = getApiClient();
    // This endpoint is paginated: the API client returns the envelope { data, meta }
    // for paginated responses (it only unwraps non-paginated { success, data }).
    const response = await client.get<ApiVehicleResponse[] | { data: ApiVehicleResponse[] }>(
      'v1/vehicles',
    );
    const list = Array.isArray(response) ? response : (response?.data ?? []);
    return list.map(mapApiVehicleToVehicle);
  }

  async getMyVehicles(): Promise<Vehicle[]> {
    const client = getApiClient();
    const response = await client.get<ApiVehicleResponse[]>('v1/vehicles/my');
    return response.map(mapApiVehicleToVehicle);
  }

  async getVehicleById(id: string): Promise<Vehicle | null> {
    const client = getApiClient();
    try {
      const vehicle = await client.get<ApiVehicleResponse>(`v1/vehicles/${id}`);
      return mapApiVehicleToVehicle(vehicle);
    } catch {
      return null;
    }
  }

  async createVehicle(vehicle: CreateVehicleDTO): Promise<Vehicle> {
    const client = getApiClient();
    const request: ApiCreateVehicleRequest = {
      plateNumber: vehicle.plateNumber,
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      color: vehicle.color,
      type: vehicle.type,
      vin: vehicle.vin,
      insurancePolicy: vehicle.insurancePolicy,
      insuranceExpiry: vehicle.insuranceExpiry,
      notes: vehicle.notes,
      isPrimary: vehicle.isPrimary,
      unitId: vehicle.unitId,
    };
    const apiVehicle = await client.post<ApiVehicleResponse>('v1/vehicles', request);
    return mapApiVehicleToVehicle(apiVehicle);
  }

  async updateVehicle(vehicle: Vehicle): Promise<Vehicle> {
    const client = getApiClient();
    const request: ApiUpdateVehicleRequest = {
      plateNumber: vehicle.plateNumber,
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      color: vehicle.color,
      type: vehicle.type,
      status: vehicle.status,
      vin: vehicle.vin,
      insurancePolicy: vehicle.insurancePolicy,
      insuranceExpiry: vehicle.insuranceExpiry,
      photoUrl: vehicle.photoUrl,
      notes: vehicle.notes,
      isPrimary: vehicle.isPrimary,
    };
    const apiVehicle = await client.put<ApiVehicleResponse>(`v1/vehicles/${vehicle.id}`, request);
    return mapApiVehicleToVehicle(apiVehicle);
  }

  async deleteVehicle(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/vehicles/${id}`);
  }

  async setPrimaryVehicle(id: string): Promise<Vehicle> {
    const client = getApiClient();
    const apiVehicle = await client.post<ApiVehicleResponse>(`v1/vehicles/${id}/set-primary`);
    return mapApiVehicleToVehicle(apiVehicle);
  }

  async getVehiclesByUser(userId: string): Promise<Vehicle[]> {
    const client = getApiClient();
    const response = await client.get<ApiVehicleResponse[]>(`v1/vehicles/user/${userId}`);
    return response.map(mapApiVehicleToVehicle);
  }
}

